import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Search, Filter, X, Tag } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { supabase } from '@/lib/supabase';
import { KnowledgeDocument } from './types';

interface TagFilter {
  tag: string;
  count: number;
  selected: boolean;
}

interface AdvancedTagSearchProps {
  onSearchResults: (results: KnowledgeDocument[]) => void;
  onFiltersChange?: (filters: string[]) => void;
}

export const AdvancedTagSearch: React.FC<AdvancedTagSearchProps> = ({
  onSearchResults,
  onFiltersChange
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [availableTags, setAvailableTags] = useState<TagFilter[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadAvailableTags();
  }, []);

  useEffect(() => {
    if (onFiltersChange) {
      onFiltersChange(selectedTags);
    }
  }, [selectedTags, onFiltersChange]);

  const loadAvailableTags = async () => {
    try {
      const { data, error } = await supabase
        .from('assistant_knowledge')
        .select('metadata');

      if (error) throw error;

      const tagCounts = new Map<string, number>();
      
      data?.forEach(doc => {
        if (doc.metadata?.tags) {
          doc.metadata.tags.forEach((tag: string) => {
            tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1);
          });
        }
      });

      const tagFilters: TagFilter[] = Array.from(tagCounts.entries())
        .map(([tag, count]) => ({
          tag,
          count,
          selected: false
        }))
        .sort((a, b) => b.count - a.count);

      setAvailableTags(tagFilters);
    } catch (error) {
      console.error('Error loading available tags:', error);
    }
  };

  const performSearch = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('assistant_knowledge')
        .select('*')
        .order('created_at', { ascending: false });

      // Apply text search if query exists
      if (searchQuery.trim()) {
        query = query.or(`title.ilike.%${searchQuery}%,document->>content.ilike.%${searchQuery}%`);
      }

      const { data, error } = await query;
      if (error) throw error;

      let results = data || [];

      // Apply tag filters
      if (selectedTags.length > 0) {
        results = results.filter(doc => {
          const docTags = doc.metadata?.tags || [];
          return selectedTags.every(selectedTag => 
            docTags.includes(selectedTag)
          );
        });
      }

      onSearchResults(results);
    } catch (error) {
      console.error('Error performing search:', error);
      onSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleTag = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const clearAllFilters = () => {
    setSelectedTags([]);
    setSearchQuery('');
  };

  const removeSelectedTag = (tag: string) => {
    setSelectedTags(prev => prev.filter(t => t !== tag));
  };

  // Auto-search when filters change
  useEffect(() => {
    performSearch();
  }, [selectedTags, searchQuery]);

  return (
    <div className="space-y-4">
      {/* Search Input */}
      <div className="flex space-x-2">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search documents by content or title..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <Popover open={showFilters} onOpenChange={setShowFilters}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="relative">
              <Filter className="h-4 w-4 mr-2" />
              Tags
              {selectedTags.length > 0 && (
                <Badge variant="destructive" className="ml-2 h-4 w-4 p-0 text-xs">
                  {selectedTags.length}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80" align="end">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Filter by Tags</h4>
                {selectedTags.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearAllFilters}
                    className="h-auto p-1 text-xs"
                  >
                    Clear All
                  </Button>
                )}
              </div>
              
              <div className="max-h-60 overflow-y-auto space-y-2">
                {availableTags.map((tagFilter) => (
                  <div
                    key={tagFilter.tag}
                    className="flex items-center space-x-2 hover:bg-muted/50 p-2 rounded cursor-pointer"
                    onClick={() => toggleTag(tagFilter.tag)}
                  >
                    <Checkbox
                      checked={selectedTags.includes(tagFilter.tag)}
                      onCheckedChange={() => toggleTag(tagFilter.tag)}
                    />
                    <div className="flex-1 flex items-center justify-between">
                      <span className="text-sm">{tagFilter.tag}</span>
                      <Badge variant="secondary" className="text-xs">
                        {tagFilter.count}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Selected Tags Display */}
      {selectedTags.length > 0 && (
        <div className="flex flex-wrap gap-2 p-3 bg-muted/30 rounded-lg">
          <div className="flex items-center text-sm text-muted-foreground mr-2">
            <Tag className="h-3 w-3 mr-1" />
            Active filters:
          </div>
          {selectedTags.map((tag) => (
            <Badge key={tag} variant="default" className="text-sm">
              {tag}
              <button
                onClick={() => removeSelectedTag(tag)}
                className="ml-2 hover:bg-destructive/20 rounded-full p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {/* Quick Tag Suggestions */}
      <div className="flex flex-wrap gap-2">
        <span className="text-sm text-muted-foreground">Quick filters:</span>
        {availableTags.slice(0, 8).map((tagFilter) => (
          <Button
            key={tagFilter.tag}
            variant={selectedTags.includes(tagFilter.tag) ? "default" : "outline"}
            size="sm"
            onClick={() => toggleTag(tagFilter.tag)}
            className="h-7 text-xs"
          >
            {tagFilter.tag}
            <Badge variant="secondary" className="ml-1 h-4 w-4 p-0 text-xs">
              {tagFilter.count}
            </Badge>
          </Button>
        ))}
      </div>
    </div>
  );
};
